import { matchPath, useLocation } from 'react-router-dom'
import { getSectionForPath, HOME_ROUTE, type Section } from './sections'
import { RESERVED_CASE_SEGMENTS, type SubView, WORKSPACES, type Workspace } from './workspaces'

export interface ActiveNav {
  pathname: string
  section: Section | undefined
  caseId: string | undefined
  activeWorkspace: Workspace | undefined
  activeSubView: SubView | undefined
  /** Best human-readable title for the current view. */
  currentTitle: string
}

const PAGE_TITLES: Array<{ pattern: string; title: string }> = [
  { pattern: HOME_ROUTE, title: 'Home' },
  { pattern: '/cases/list', title: 'All cases' },
  { pattern: '/data', title: 'Data' },
  { pattern: '/admin/labels', title: 'Labels' },
  { pattern: '/admin/users', title: 'Users' },
  { pattern: '/admin/schema', title: 'Schema' },
  { pattern: '/admin/pipelines', title: 'Pipelines' },
  { pattern: '/admin/processing', title: 'Processing' },
  { pattern: '/admin/extraction', title: 'Processing' },
  { pattern: '/settings/*', title: 'Settings' },
  { pattern: '/settings', title: 'Settings' },
]

function pageTitleFor(pathname: string): string {
  for (const { pattern, title } of PAGE_TITLES) {
    if (matchPath(pattern, pathname)) return title
  }
  return ''
}

export function useActiveNav(): ActiveNav {
  const location = useLocation()
  const pathname = location.pathname

  const section = getSectionForPath(pathname)
  const caseMatch = matchPath({ path: '/cases/:caseId/*', end: false }, pathname)
  const rawCaseId = caseMatch?.params.caseId
  const caseId = rawCaseId && !RESERVED_CASE_SEGMENTS.has(rawCaseId) ? rawCaseId : undefined

  let activeWorkspace: Workspace | undefined
  let activeSubView: SubView | undefined

  if (caseId) {
    // Last segment after /cases/:caseId/ is the flat sub-view path.
    const seg = pathname.match(/\/cases\/[^/]+\/([^/?#]+)/)?.[1]
    if (seg) {
      for (const ws of WORKSPACES) {
        const sv = ws.subViews.find(s => s.path === seg)
        if (sv) {
          activeWorkspace = ws
          activeSubView = sv
          break
        }
      }
    }
  }

  const currentTitle =
    activeSubView?.label ?? activeWorkspace?.label ?? pageTitleFor(pathname) ?? ''

  return { pathname, section, caseId, activeWorkspace, activeSubView, currentTitle }
}
